"use client";

import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Phenoliccompliancestandards() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: diagramRef,
    isVisible: diagramVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: specsRef,
    isVisible: specsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    number: "01",
    title: "Material Selection",
    description: "We source industrial-grade phenolic and acrylic substrates that meet strict NEMA and MIL-SPEC requirements."
  }, {
    number: "02",
    title: "Precision Engraving",
    description: "High-speed laser systems ensure clean, legible characters that resist chemical and environmental degradation."
  }, {
    number: "03",
    title: "Compliance Verification",
    description: "Every tag is verified against your provided specifications to ensure full regulatory and safety compliance."
  }, {
    number: "04",
    title: "Durable Finishing",
    description: "Edges are beveled and surfaces are treated to prevent fraying and ensure long-term structural integrity."
  }, {
    number: "05",
    title: "Quality Documentation",
    description: "We provide full traceability and material certification for every batch of identification tags produced."
  }, {
    number: "06",
    title: "Secure Logistics",
    description: "Parts are organized, protected, and shipped to your site ready for immediate installation."
  }];
  const callouts = [{
    x: 18,
    y: 22
  }, {
    x: 82,
    y: 18
  }, {
    x: 14,
    y: 50
  }, {
    x: 86,
    y: 50
  }, {
    x: 22,
    y: 78
  }, {
    x: 78,
    y: 82
  }];
  const specs = [{
    value: "0.0625\"",
    label: "STANDARD THICKNESS"
  }, {
    value: "MIL-SPEC",
    label: "COMPLIANCE GRADE"
  }, {
    value: "100%",
    label: "VERIFIED ACCURACY"
  }];
  return <section data-section-id="3497" id="phenoliccompliancestandards" className="relative overflow-hidden py-32 md:py-48 lg:py-[200px] bg-[#0F1B2D] text-foreground border-t-2 border-[#C9A227]">
      {}
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
      backgroundImage: "linear-gradient(to right, #C9A227 1px, transparent 1px), linear-gradient(to bottom, #C9A227 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
        {}
        <div ref={headerRef} className={`mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-default text-xs md:text-sm uppercase tracking-[0.2em] mb-6 md:mb-8 text-primary">
            Standards — 05
          </p>
          <h2 className="font-default font-bold leading-[0.95] tracking-tight max-w-[14ch] text-[#FFFFFF]" style={{
          fontSize: "clamp(56px, 8vw, 120px)"
        }}>
            Phenolic
            <br />
            <span className="text-primary">/ Compliance.</span>
          </h2>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-24 md:mb-32">
          {}
          <div ref={diagramRef} className={`lg:col-span-7 lg:col-start-1 transition-all duration-1000 ease-out ${diagramVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="relative aspect-square w-full max-w-[640px] mx-auto">
              {}
              <svg viewBox="0 0 600 600" className="absolute inset-0 w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                {}
                <circle cx="300" cy="300" r="260" stroke="#C9A227" strokeWidth="0.6" opacity="0.5" />
                <circle cx="300" cy="300" r="240" stroke="#FFFFFF" strokeWidth="0.4" opacity="0.3" />
                {}
                <circle cx="300" cy="300" r="210" stroke="#C9A227" strokeWidth="0.5" opacity="0.4" />
                {Array.from({
                length: 36
              }).map((_, i) => {
                const angle = i * 10 * Math.PI / 180;
                const x1 = 300 + Math.cos(angle) * 180;
                const y1 = 300 + Math.sin(angle) * 180;
                const x2 = 300 + Math.cos(angle) * 210;
                const y2 = 300 + Math.sin(angle) * 210;
                return <line key={i} data-index={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#C9A227" strokeWidth="0.3" opacity="0.4" />;
              })}
                {}
                <circle cx="300" cy="300" r="170" stroke="#FFFFFF" strokeWidth="0.5" opacity="0.6" />
                <circle cx="300" cy="300" r="160" stroke="#FFFFFF" strokeWidth="0.3" opacity="0.4" />
                {}
                {Array.from({
                length: 8
              }).map((_, i) => <circle key={`trace-${i}`} data-index={i} cx="300" cy="300" r={155 - i * 18} stroke="#C9A227" strokeWidth="0.4" opacity={0.7 - i * 0.05} strokeDasharray="2 3" />)}
                {}
                {Array.from({
                length: 12
              }).map((_, i) => {
                const angle = i * 30 * Math.PI / 180;
                const x = 300 + Math.cos(angle) * 135;
                const y = 300 + Math.sin(angle) * 135;
                return <g key={`mag-${i}`} data-index={i}>
                      <rect x={x - 6} y={y - 2} width="12" height="4" fill="#C9A227" opacity="0.6" transform={`rotate(${i * 30} ${x} ${y})`} />
                    </g>;
              })}
                {}
                <circle cx="300" cy="300" r="40" stroke="#FFFFFF" strokeWidth="0.6" opacity="0.7" />
                <circle cx="300" cy="300" r="28" stroke="#C9A227" strokeWidth="0.5" opacity="0.8" />
                <circle cx="300" cy="300" r="3" fill="#C9A227" opacity="0.9" />

                {}
                {callouts.map((c, i) => {
                const cx = c.x / 100 * 600;
                const cy = c.y / 100 * 600;
                const dx = cx - 300;
                const dy = cy - 300;
                const len = Math.sqrt(dx * dx + dy * dy);
                const innerX = 300 + dx / len * 130;
                const innerY = 300 + dy / len * 130;
                const isActive = activeIndex === i;
                return <line key={`anno-${i}`} data-index={i} x1={innerX} y1={innerY} x2={cx} y2={cy} stroke={isActive ? "#E8E4DC" : "#C9A227"} strokeWidth={isActive ? "1" : "0.5"} opacity={isActive ? "1" : "0.6"} strokeDasharray="3 2" className="transition-all duration-300" />;
              })}
              </svg>

              {}
              {callouts.map((c, i) => {
              const isActive = activeIndex === i;
              return <div key={`callout-${i}`} data-index={i} className="absolute -translate-x-1/2 -translate-y-1/2 transition-all duration-300" style={{
                left: `${c.x}%`,
                top: `${c.y}%`
              }}>
                    <div className={`flex items-center gap-2 transition-all duration-300 ${isActive ? "scale-110" : ""}`}>
                      <span className="flex items-center justify-center w-7 h-7 rounded-lg font-default text-[10px] font-medium transition-all duration-300" style={{
                    backgroundColor: isActive ? "#C9A227" : "#FFFFFF",
                    color: isActive ? "#000000" : "#000000"
                  }}>
                        {features[i].number}
                      </span>
                      <span className="font-default text-[11px] uppercase tracking-wider whitespace-nowrap transition-colors duration-300" style={{
                    color: isActive ? "#C9A227" : "#E8E4DC"
                  }}>
                        {features[i].title}
                      </span>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {}
          <div ref={cardsRef} className={`lg:col-span-5 lg:col-start-8 flex flex-col gap-4 transition-all duration-1000 ease-out ${cardsVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {features.map((feature, i) => {
            const isActive = activeIndex === i;
            return <Card key={i} data-index={i} onMouseEnter={() => setActiveIndex(i)} onMouseLeave={() => setActiveIndex(null)} className="cursor-pointer border transition-all duration-300 py-5 bg-card" style={{
              borderColor: isActive ? "#C9A227" : "#1A2A40"
            }}>
                  <CardContent className="px-5 flex gap-5 items-start">
                    <span className="font-default text-xs mt-1 transition-colors duration-300 text-primary">
                      {feature.number}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-default font-bold mb-1.5 transition-colors duration-300 text-foreground" style={{
                    fontSize: "22px",
                    lineHeight: "1.2"
                  }}>
                        {feature.title}
                      </h3>
                      <p className="font-default font-normal text-foreground/80" style={{
                    fontSize: "15px",
                    lineHeight: "1.55"
                  }}>
                        {feature.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {}
        <div ref={specsRef} className={`border-t pt-16 md:pt-20 transition-all duration-1000 ease-out border-foreground/20 ${specsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {specs.map((spec, i) => <div key={i} data-index={i} className="flex flex-col">
                <span className="font-default font-bold leading-none mb-4 text-primary" style={{
              fontSize: "clamp(48px, 6vw, 84px)",
              letterSpacing: "-0.02em"
            }}>
                  {spec.value}
                </span>
                <span className="font-default text-[11px] uppercase tracking-[0.18em] text-foreground/70">
                  {spec.label}
                </span>
              </div>)}
          </div>

          {}
          <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="font-default font-normal max-w-md text-[#E8E4DC]" style={{
            fontSize: "16px"
          }}>
              Permanent identification solutions engineered for industrial environments. Specified by name.
            </p>
            
          </div>
        </div>
      </div>
    </section>;
}